import React, { useEffect, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { useNavigate } from 'react-router-dom';

import { LOAD_MORE_PAGE_SIZE, VALIDATION_POLL_INTERVAL_MS } from '~constants/common';

import { useSearch } from '~libs/searchParams';

import { Button, ClickableIcon, Toolbar } from '~components';
import { SearchBar, SearchProps } from '~components/form-fields/search';
import Icon, { faMagnifyingGlass, faPlus } from '~components/icon';
import { TableView } from '~components/table-view';

import columnsFactory from '~support-at-home/forms/claims/claimsList';

import { getAll as getStatuses } from '~appointments/actions/statuses';
import { setTitle } from '~main/actions/login';
import { useGetClaimsQuery, useValidateClaimMutation } from '~support-at-home/actions/claims';

import ClaimsWizard from './claims-wizard';

const searchFields: SearchProps = {
  start_date: {
    defaultShow: true,
    type: 'date-range',
    field: 'start_date',
    caption: 'Start Date',
  },
};

const Claims: React.FC<{ isActive: boolean }> = ({ isActive }) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [searchBarOpen, setSearchBarOpen] = useState<boolean>(false);
  const [searchParams, setSearchParams] = useSearch('claims-search', { limit: LOAD_MORE_PAGE_SIZE });
  const [claimsWizardOpen, setClaimsWizardOpen] = useState<boolean>(false);

  const handleSearchSubmit = (values: Record<string, any>) => setSearchParams({ ...values, limit: LOAD_MORE_PAGE_SIZE });

  const handleLoadMore = () => {
    const currentLimit = Number(searchParams.limit) || LOAD_MORE_PAGE_SIZE;
    setSearchParams({ ...searchParams, limit: currentLimit + LOAD_MORE_PAGE_SIZE });
  };

  // RTK Hooks
  const { data, isFetching: isLoading, refetch: refetchClaims } = useGetClaimsQuery(searchParams);
  const [validateClaim] = useValidateClaimMutation();

  const claims = data?.rows || [];
  const total = data?.total;

  useEffect(() => {
    if (isActive) {
      setTitle('Claims');
    }
    dispatch(getStatuses());
  }, [isActive]);

  useEffect(() => {
    const hasValidationsInFlight = claims.some((claim) => claim.is_validating);
    if (!hasValidationsInFlight) return;

    const interval = setInterval(() => {
      refetchClaims();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [claims, refetchClaims]);

  const onClaimClick = (id: string) => {
    navigate(`/support-at-home/claims/${id}`);
  };

  const handleValidateClaim = (id: string) => {
    validateClaim({ id });
  };

  const columns = columnsFactory(onClaimClick, handleValidateClaim);

  return (
    <div className="p-2 claims">
      <Toolbar
        id="claims"
        rightCluster={
          <>
            <ClickableIcon
              className="me-2"
              icon={faMagnifyingGlass}
              id="claims-search-button"
              onClick={() => setSearchBarOpen(!searchBarOpen)}
              toolTip="Toggle Search Bar"
            />
            <Button dataCy="claims-new-button" size="sm" color="success" onClick={() => setClaimsWizardOpen(true)}>
              <Icon className="me-2" icon={faPlus} />
              New
            </Button>
          </>
        }
      />
      <SearchBar
        isOpen={searchBarOpen}
        fields={searchFields}
        initialValues={searchParams}
        onSubmit={handleSearchSubmit}
      />
      <TableView
        id="claims-table"
        columns={columns}
        data={claims}
        isLoading={isLoading}
        emptyMessage="No claims found"
        readonly
        striped
        allowPagination
        totalCount={total}
        onLoadMore={handleLoadMore}
      />
      <ClaimsWizard isOpen={claimsWizardOpen} setIsOpen={setClaimsWizardOpen} />
    </div>
  );
};

export default Claims;
